export const BASE_URL = 'https://qa.api.tripnour.com';

let isRefreshing = false;
let failedQueue = [];
// 
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export const customFetch = async (url, options = {}) => {
    try {
        const response = await window.fetch(url, options);
        if (response.status === 401 && !url.toString().includes('/login') && !url.toString().includes('/signup')) {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                if (isRefreshing) {
                    return new Promise(function (resolve, reject) {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        const newOptions = { ...options };
                        newOptions.headers = {
                            ...newOptions.headers,
                            'Authorization': `Bearer ${token}`
                        };
                        return window.fetch(url, newOptions);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                isRefreshing = true;

                try {
                    const refreshResponse = await window.fetch(`${BASE_URL}/refresh`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${refreshToken}`,
                            'accept': 'application/json',
                        },
                    });

                    if (refreshResponse.ok) {
                        const refreshData = await refreshResponse.json();
                        if (refreshData.access_token) {
                            localStorage.setItem('access_token', refreshData.access_token);
                            if (refreshData.refresh_token) {
                                localStorage.setItem('refresh_token', refreshData.refresh_token);
                            }

                            processQueue(null, refreshData.access_token);

                            // Retry original request
                            const newOptions = { ...options };
                            newOptions.headers = {
                                ...newOptions.headers,
                                'Authorization': `Bearer ${refreshData.access_token}`
                            };
                            return await window.fetch(url, newOptions);
                        }
                    }
                } catch (e) {
                    console.error("Token refresh failed", e);
                } finally {
                    isRefreshing = false;
                }
            }

            // If no refresh token or refresh failed, clear tokens and redirect to signin
            processQueue(new Error("Refresh failed"), null);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/signin';
            // Return a promise that never resolves so execution halts while the browser navigates
            return new Promise(() => { });
        }

        if (!response.ok) {
            // Clone response to read error text without consuming the original body
            const clonedResponse = response.clone();
            clonedResponse.text().then(errorText => {
                logFrontendError({
                    api_endpoint: url.toString(),
                    error_message: `API Error ${response.status}: ${errorText}`
                });
            }).catch(() => { });
        }

        return response;
    } catch (error) {
        logFrontendError({
            api_endpoint: url.toString(),
            error_message: error.message || "Network request failed",
            stack_trace: error.stack || ""
        });
        throw error;
    }
};

// Override global fetch for this file to handle 401s automatically
const fetch = customFetch;

// ─── Auth Helper ─────────────────────────────────────────────────────────────
/**
* Returns the Authorization header object with the stored JWT token.
* Throws if no token is found (unauthenticated state).
*/
const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    // We don't throw here to avoid blocking the fetch call entirely.
    // If the token is missing, the backend will return 401, which is easier to handle.
    return {
        'Authorization': `Bearer ${token || ''}`,
        'accept': 'application/json',
    };
};

// ─── API Caching Layer ────────────────────────────────────────────────────────
let apiCache = {
    hotLeads: null,
    highValueProspects: null,
    inProgressLeads: null,
    dropoutLeads: null,
    hitlLeads: null,
    travelPlans: null
};

export const clearApiCache = () => {
    apiCache = {
        hotLeads: null,
        highValueProspects: null,
        inProgressLeads: null,
        dropoutLeads: null,
        hitlLeads: null,
        travelPlans: null
    };
};

const extractErrorMessage = (errorData, defaultMessage) => {
    if (!errorData || !errorData.detail) return defaultMessage;
    if (Array.isArray(errorData.detail)) {
        return errorData.detail.map(err => err.msg || err).join('; ');
    }
    if (typeof errorData.detail === 'string') {
        return errorData.detail;
    }
    if (typeof errorData.detail === 'object') {
        return errorData.detail.msg || JSON.stringify(errorData.detail);
    }
    return defaultMessage;
};

// ─── Auth APIs (no token required) ───────────────────────────────────────────
export const loginApi = async (username, password, orgCode = '') => {
    // Clear cache on fresh login to prevent cross-contamination
    clearApiCache();

    const response = await fetch(`${BASE_URL}/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            username: username,
            password: password,
            org_code: orgCode
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errorData, 'Login failed. Please check your credentials.'));
    }

    return await response.json();
};

export const signupApi = async (userData) => {
    const response = await fetch(`${BASE_URL}/signup`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errorData, 'Signup failed. Please try again.'));
    }

    return await response.json();
};

export const forgotPasswordApi = async (email, orgCode) => {
    const response = await fetch(`${BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email, org_code: orgCode }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errorData, 'Failed to send password reset email.'));
    }

    return await response.json();
};

export const resetPasswordApi = async (token, newPassword, confirmPassword) => {
    const response = await fetch(`${BASE_URL}/reset-password`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            token: token,
            new_password: newPassword,
            confirm_password: confirmPassword,
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errorData, 'Failed to reset password.'));
    }

    return await response.json();
};

export const changePasswordApi = async (currentPassword, newPassword, confirmPassword) => {
    const response = await fetch(`${BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
            confirm_password: confirmPassword,
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(errorData, 'Failed to change password.'));
    }

    return await response.json();
};

export const getHotLeads = async (forceRefetch = false, tripType = '', actions = []) => {
    if (!forceRefetch && !tripType && (!actions || actions.length === 0) && apiCache.hotLeads) {
        return apiCache.hotLeads;
    }
    const params = [];
    if (tripType) params.push(`trip_type=${encodeURIComponent(tripType)}`);
    if (actions) {
        const actionArray = Array.isArray(actions) ? actions : [actions];
        actionArray.forEach(a => {
            if (a) params.push(`actions=${encodeURIComponent(a)}`);
        });
    }
    const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await fetch(`${BASE_URL}/hot-leads${queryStr}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch hot leads');
    const data = await response.json();
    if (!tripType && (!actions || actions.length === 0)) {
        apiCache.hotLeads = data;
    }
    return data;
};

export const getHighValueProspects = async (forceRefetch = false, tripType = '', actions = []) => {
    if (!forceRefetch && !tripType && (!actions || actions.length === 0) && apiCache.highValueProspects) {
        return apiCache.highValueProspects;
    }
    const params = [];
    if (tripType) params.push(`trip_type=${encodeURIComponent(tripType)}`);
    if (actions) {
        const actionArray = Array.isArray(actions) ? actions : [actions];
        actionArray.forEach(a => {
            if (a) params.push(`actions=${encodeURIComponent(a)}`);
        });
    }
    const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await fetch(`${BASE_URL}/high-value-prospects${queryStr}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch high prospects');
    const data = await response.json();
    if (!tripType && (!actions || actions.length === 0)) {
        apiCache.highValueProspects = data;
    }
    return data;
};

export const getInProgressLeads = async (forceRefetch = false, tripType = '', actions = []) => {
    if (!forceRefetch && !tripType && (!actions || actions.length === 0) && apiCache.inProgressLeads) {
        return apiCache.inProgressLeads;
    }
    const params = [];
    if (tripType) params.push(`trip_type=${encodeURIComponent(tripType)}`);
    if (actions) {
        const actionArray = Array.isArray(actions) ? actions : [actions];
        actionArray.forEach(a => {
            if (a) params.push(`actions=${encodeURIComponent(a)}`);
        });
    }
    const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await fetch(`${BASE_URL}/in-progress${queryStr}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch in progress leads');
    const data = await response.json();
    if (!tripType && (!actions || actions.length === 0)) {
        apiCache.inProgressLeads = data;
    }
    return data;
};

export const getDropoutLeads = async (forceRefetch = false, tripType = '', actions = []) => {
    if (!forceRefetch && !tripType && (!actions || actions.length === 0) && apiCache.dropoutLeads) {
        return apiCache.dropoutLeads;
    }
    const params = [];
    if (tripType) params.push(`trip_type=${encodeURIComponent(tripType)}`);
    if (actions) {
        const actionArray = Array.isArray(actions) ? actions : [actions];
        actionArray.forEach(a => {
            if (a) params.push(`actions=${encodeURIComponent(a)}`);
        });
    }
    const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await fetch(`${BASE_URL}/leads/dropout${queryStr}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch dropout leads');
    const data = await response.json();
    if (!tripType && (!actions || actions.length === 0)) {
        apiCache.dropoutLeads = data;
    }
    return data;
};

export const getHitlLeads = async (forceRefetch = false, tripType = '', actions = []) => {
    if (!forceRefetch && !tripType && (!actions || actions.length === 0) && apiCache.hitlLeads) {
        return apiCache.hitlLeads;
    }
    const params = [];
    if (tripType) params.push(`trip_type=${encodeURIComponent(tripType)}`);
    if (actions) {
        const actionArray = Array.isArray(actions) ? actions : [actions];
        actionArray.forEach(a => {
            if (a) params.push(`actions=${encodeURIComponent(a)}`);
        });
    }
    const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await fetch(`${BASE_URL}/hitl-leads${queryStr}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch HITL leads');
    const data = await response.json();
    if (!tripType && (!actions || actions.length === 0)) {
        apiCache.hitlLeads = data;
    }
    return data;
};

export const getLeadFullDetails = async (leadId, tripId = '') => {
    const url = new URL(`${BASE_URL}/leads/${encodeURIComponent(leadId)}/full-details`);
    if (tripId) url.searchParams.append('trip_id', tripId);

    const response = await fetch(url.toString(), {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch lead full details');
    return await response.json();
};

export const sendSalesReply = async (leadId, message, param3 = '', param4 = '') => {
    let sessionId = null;
    let channel = 'chat';

    if (['email', 'chat', 'whatsapp'].includes(String(param3).toLowerCase())) {
        channel = param3;
        sessionId = param4 || null;
    } else {
        sessionId = param3 || null;
        channel = param4 || 'chat';
    }

    const url = `${BASE_URL}/leads/${encodeURIComponent(leadId)}/sales_reply`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            message: message,
            channel: channel,
            session_id: sessionId
        }),
    });

    if (!response.ok) throw new Error('Failed to send sales reply');
    return await response.json();
};

export const getCallNowAction = async (leadName) => {
    const response = await fetch(`${BASE_URL}/actions/call-now/${encodeURIComponent(leadName)}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch call now action');
    return await response.json();
};

export const getSendNowAction = async (leadName) => {
    const response = await fetch(`${BASE_URL}/actions/send-now/${encodeURIComponent(leadName)}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch send now action');
    return await response.json();
};

export const getFollowUpAction = async (leadName) => {
    const response = await fetch(`${BASE_URL}/actions/follow-up/${encodeURIComponent(leadName)}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch follow up action');
    return await response.json();
};

export const getSendMeetingRequestAction = async (leadName) => {
    const response = await fetch(`${BASE_URL}/actions/send-meeting-request/${encodeURIComponent(leadName)}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch send meeting request action');
    return await response.json();
};

export const getEmailActions = async (email, page = 1, limit = 10) => {
    const response = await fetch(
        `${BASE_URL}/lead-management/email-actions/${email}?page=${page}&limit=${limit}`,
        { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch email actions');
    return await response.json();
};

export const getLeadEngagementAnalysis = async (email) => {
    const response = await fetch(
        `${BASE_URL}/lead-management/engagement-analysis/${email}`,
        { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch engagement analysis');
    return await response.json();
};

export const getLeadInteractions = async (email) => {
    const response = await fetch(
        `${BASE_URL}/lead-management/interactions/${email}`,
        { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch interactions');
    return response.json();
};

export const uploadLeadsApi = async (file) => {
    const token = localStorage.getItem('access_token');

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}/trigger_welcome_from_excel`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token || ''}`,
            accept: 'application/json',
        },
        body: formData,
    });

    if (!response.ok) throw new Error('Upload failed');
    clearApiCache();
    return response.json();
};

// ─── HITL APIs (token required) ───────────────────────────────────────────────
export const getHitlItem = async (hitlId) => {
    const url = `${BASE_URL}/hitl/item/${encodeURIComponent(hitlId)}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch HITL item');
    return await response.json();
};

export const submitHitlAction = async (leadId, actionData, tripId = null) => {
    let url = `${BASE_URL}/hitl/${encodeURIComponent(leadId)}/action`;
    if (tripId) {
        url += `?trip_id=${encodeURIComponent(tripId)}`;
    }
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
    });
    if (!response.ok) throw new Error('Failed to submit HITL action');
    clearApiCache();
    return await response.json();
};

export const generateHitlDraft = async (hitlId, salesAgentInput, channel = 'email') => {
    const url = `${BASE_URL}/hitl/generate-draft`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            hitl_id: hitlId,
            sales_agent_input: salesAgentInput,
            channel,
        }),
    });
    if (!response.ok) throw new Error('Failed to generate HITL draft');
    clearApiCache();
    return await response.json();
};

export const sendHitlDraft = async (hitlId, channel = 'email', actionTaken = null) => {
    const url = `${BASE_URL}/hitl/send-draft`;
    const effectiveAction = actionTaken || (channel === 'whatsapp' ? 'Send WhatsApp msg' : 'Send Email');
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            hitl_id: hitlId,
            channel,
            action_taken: effectiveAction
        }),
    });
    if (!response.ok) throw new Error('Failed to send HITL draft');
    clearApiCache();
    return await response.json();
};

export const getTravelPlans = async (forceRefetch = false) => {
    if (!forceRefetch && apiCache.travelPlans) {
        return apiCache.travelPlans;
    }
    const response = await fetch(
        `${BASE_URL}/travel_plans`,
        {
            headers: getAuthHeaders(),
        }
    );
    if (!response.ok) throw new Error('Failed to fetch travel plans');
    const data = await response.json();
    apiCache.travelPlans = data;
    return data;
};


export const addHitlContext = async (hitlId, agentNote) => {
    const url = `${BASE_URL}/hitl/add-context`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            hitl_id: hitlId,
            agent_note: agentNote,
            action_taken: "Add context"
        }),
    });
    if (!response.ok) throw new Error('Failed to add HITL context');
    clearApiCache();
    return await response.json();
};

export const getPendingHitlRequests = async () => {
    const orgId = localStorage.getItem('org_id') || '';
    const queryStr = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
    const url = `${BASE_URL}/hitl/pending${queryStr}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch pending HITL requests');
    return await response.json();
};

export const logFrontendError = async (errorData) => {
    try {
        const token = localStorage.getItem('access_token');
        const orgId = localStorage.getItem('org_id') || "";
        const username = localStorage.getItem('username') || "";

        const payload = {
            org_id: orgId,
            lead_id: errorData.lead_id || "",
            session_id: errorData.session_id || "",
            username: username,
            api_endpoint: errorData.api_endpoint || "",
            error_message: errorData.error_message || "Unknown error",
            stack_trace: errorData.stack_trace || "",
            url: window.location.href,
            additional_info: errorData.additional_info || {}
        };

        await window.fetch(`${BASE_URL}/frontend-logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(payload)
        });
    } catch (e) {
        console.error("Failed to log frontend error:", e);
    }
};

export const uploadProductImages = async (productId, imageUrls) => {
    const response = await fetch(`${BASE_URL}/travel_products/${encodeURIComponent(productId)}/images`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            image_urls: imageUrls,
        }),
    });

    if (!response.ok) throw new Error('Failed to upload product images');
    return await response.json();
};

export const getEmailConversation = async (leadId, tripId = '') => {
    const url = new URL(`${BASE_URL}/lead-management/email-conversation/${encodeURIComponent(leadId)}`);
    if (tripId) url.searchParams.append('trip_id', tripId);
    const response = await fetch(url.toString(), {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch email conversation');
    return await response.json();
};


export const getNotes = async (leadId, tripId = '') => {
    const url = new URL(`${BASE_URL}/lead-management/notes/${encodeURIComponent(leadId)}`);
    if (tripId) url.searchParams.append('trip_id', tripId);
    const response = await fetch(url.toString(), {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch notes');
    return await response.json();
};

export const addNote = async (leadId, noteData) => {
    const url = `${BASE_URL}/lead-management/notes/${encodeURIComponent(leadId)}`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
    });
    if (!response.ok) throw new Error('Failed to save note');
    return await response.json();
};

export const getWhatsappChatMessages = async (leadId, tripId = '') => {
    const url = new URL(`${BASE_URL}/whatsapp/chat-messages`);
    if (leadId) url.searchParams.append('lead_id', leadId);
    if (tripId) url.searchParams.append('trip_id', tripId);
    const response = await fetch(url.toString(), {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch WhatsApp chat messages');
    return await response.json();
};

export const downloadLeadsExcelApi = async () => {
    const response = await fetch(`${BASE_URL}/export-leads`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to export leads');

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'leads_export.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
};

export const getChannelsConfig = async () => {
    let adminId = "";
    let orgId = localStorage.getItem('org_id') || "";
    try {
        let userId = localStorage.getItem('user_id');
        if (userId) {
            const parsed = JSON.parse(userId);
            adminId = typeof parsed === 'string' ? parsed : (parsed.user_id || "");
        }
        if (!adminId) {
            let uname = localStorage.getItem('username');
            if (uname) {
                const parsedUname = JSON.parse(uname);
                adminId = typeof parsedUname === 'string' ? parsedUname : uname;
            }
        }
    } catch (e) { }

    const response = await fetch(`${BASE_URL}/admin-configs/${adminId}/${orgId}/preferred-channels`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch preferred channels config');
    return await response.json();
};

export const updateChannelsConfig = async (adminId, orgId, channelsData) => {
    const response = await fetch(`${BASE_URL}/admin-configs/${adminId}/${orgId}/preferred-channels`, {
        method: 'PUT',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferred_communication_channel: channelsData }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update preferred channels config');
    }
    return await response.json();
};

export const getPreferredChannelsConfig = async () => {
    let adminId = "";
    let orgId = localStorage.getItem('org_id') || "";
    try {
        let userId = localStorage.getItem('user_id');
        if (userId) {
            const parsed = JSON.parse(userId);
            adminId = typeof parsed === 'string' ? parsed : (parsed.user_id || "");
        }
        if (!adminId) {
            let uname = localStorage.getItem('username');
            if (uname) {
                const parsedUname = JSON.parse(uname);
                adminId = typeof parsedUname === 'string' ? parsedUname : uname;
            }
        }
    } catch (e) { }

    const response = await fetch(`${BASE_URL}/admin-configs/${adminId}/${orgId}/preferred-channels`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch preferred channels config');
    return await response.json();
};

export const updatePreferredChannelsConfig = async (adminId, orgId, preferredChannelsData) => {
    const response = await fetch(`${BASE_URL}/admin-configs/${adminId}/${orgId}/preferred-channels`, {
        method: 'PUT',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferred_communication_channel: preferredChannelsData }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update preferred channels config');
    }
    return await response.json();
};
